import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from './config'

type TOptions = Record<string, unknown> | undefined

/** Memoized string results for `t(key, opts)` across modals; cleared on language / bundle changes. */
const translationStringCache = new Map<string, string>()

let cacheInvalidationBound = false

function bindTranslationCacheInvalidation() {
  if (cacheInvalidationBound) return
  cacheInvalidationBound = true
  const clear = () => translationStringCache.clear()
  i18n.on('languageChanged', clear)
  i18n.on('loaded', clear)
}

function stableOptionsKey(opts: TOptions): string {
  if (!opts) return ''
  const keys = Object.keys(opts)
  if (keys.length === 0) return ''
  try {
    keys.sort()
    const norm: Record<string, unknown> = {}
    for (const k of keys) norm[k] = opts[k]
    return JSON.stringify(norm)
  } catch {
    return ''
  }
}

/**
 * Same API as `useTranslation` from react-i18next, for building / land modals.
 *
 * - Locale JSON is loaded via cached `fetchLocaleJson` in `./config` (HTTP + in-flight dedupe).
 * - String `t()` results are memoized in memory (shared across modal instances) until language or resources change.
 */
export function useModalTranslation(...args: Parameters<typeof useTranslation>) {
  bindTranslationCacheInvalidation()
  const result = useTranslation(...args)
  const { t: tRaw } = result

  // When i18n reports new resources without `loaded` (edge cases), drop memo so new copy wins.
  useEffect(() => {
    const onAdded = () => translationStringCache.clear()
    i18n.on('added', onAdded)
    i18n.on('removed', onAdded)
    return () => {
      i18n.off('added', onAdded)
      i18n.off('removed', onAdded)
    }
  }, [])

  const t = useCallback(
    ((key: string | string[], opts?: TOptions) => {
      const k = Array.isArray(key) ? key.join('\u0001') : key
      const lng = i18n.language
      // `v2` busts entries cached before we skipped storing i18next "missing key" fallbacks.
      const cacheKey = `v2::${lng}::${k}::${stableOptionsKey(opts)}`
      const hit = translationStringCache.get(cacheKey)
      if (hit !== undefined) return hit

      const out = tRaw(key, opts as never)
      if (typeof out === 'string') {
        // Do not cache unresolved keys (e.g. first render before locale bundle is ready).
        if (out !== k) translationStringCache.set(cacheKey, out)
        return out
      }
      return out
    }) as (typeof result)['t'],
    [tRaw],
  )

  return { ...result, t }
}
